import axios, { AxiosRequestConfig } from 'axios';
import { getOrExtractPublicKey } from './publicKey';
import { Web3ProviderInterface } from '../types/web3';

// Configure axios globally for cookie handling
axios.defaults.withCredentials = true;

// Config array for endpoints that need public key in request body
const ENDPOINTS_WITH_PUBLIC_KEY = [
  '/thread/actions/game/*/attestJoinGame',
  '/player/proposal/*/attestProposal',
  '/player/vote/*/attestVote',
  '/user/profile/store',
];

// Check if an endpoint matches any pattern in the config
const shouldAddPublicKey = (url: string): boolean => {
  return ENDPOINTS_WITH_PUBLIC_KEY.some((pattern) => {
    const regexPattern = pattern.replace(/\*/g, '.*');
    return new RegExp(regexPattern).test(url);
  });
};

export const http = async (
  config: AxiosRequestConfig<any>,
  withoutAuth = false,
  web3Context?: Web3ProviderInterface
) => {
  const defaultHeaders = config.headers ?? {};

  // Extract privy token for logging purposes only
  const privyToken = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('privy-id-token='));

  let requestData = config.data;

  if (
    shouldAddPublicKey(config.url || '') &&
    config.method?.toUpperCase() === 'POST' &&
    config.data &&
    typeof config.data === 'object'
  ) {
    try {
      if (web3Context?.account && web3Context?.walletClient && web3Context?.currentChain) {
        const publicKeyData = await getOrExtractPublicKey(
          web3Context.walletClient,
          web3Context.account,
          web3Context.currentChain.id
        );

        requestData = {
          ...config.data,
          publicKey: publicKeyData.publicKey,
        };
      }
    } catch (error) {
      console.error('Error getting public key for request:', error);
    }
  }

  const options = {
    ...config,
    data: requestData,
    headers: {
      'X-Requested-With': 'XMLHttpRequest', // Required for CSRF protection
      ...defaultHeaders,
    },
    withCredentials: !withoutAuth,
  };

  console.log('HTTP Request:', {
    url: options.url,
    method: options.method,
    withCredentials: options.withCredentials,
    withoutAuth,
    hasPrivyToken: !!privyToken,
    hasPublicKey: requestData !== config.data,
    manualCookie: !!privyToken && !withoutAuth,
  });

  try {
    const response = await axios(options);
    console.log('HTTP Success:', options.url, response.status);
    return response;
  } catch (error: any) {
    console.log('HTTP Error:', {
      url: options.url,
      status: error?.response?.status,
      data: error?.response?.data,
      withCredentials: error?.config?.withCredentials,
    });
    throw error;
  }
};

export const queryPublic = (uri: string) => {
  return axios({
    method: 'GET',
    url: uri,
    withCredentials: false,
  });
};

export { axios };
